import { useEffect } from 'react'
import styled from 'styled-components/macro'
import { useMutation, useQuery } from '@apollo/client'
import { useNavigate, useParams } from 'react-router-dom'
import { Container } from '@mui/material'

import { PUBLIC_CERTIFICATE_ANSWER_QUESTION_MUTATION, PUBLIC_CERTIFICATE_DETAILS_QUERY } from 'src/queries/PublicCertificate'
import { CertificateState, Mutation, MutationAnswerQuestionArgs, Query, QueryPublicCertificateArgs } from 'src/types/graphql'
import { spacing } from 'src/utils/styled'
import { GenericLoading } from 'src/components/Utils/GenericLoading/GenericLoading'
import { GenericError } from 'src/components/Utils/GenericError/GenericError'
import { CertificateQuizQuestion } from 'src/components/CertificateQuiz/CertificateQuizQuestion/CertificateQuizQuestion'
import { CertificateQuestionAnswerType } from 'src/types/CertificateQuestion'
import { CertificateQuizQuestionHeader } from 'src/components/CertificateQuiz/CertificateQuizQuestionHeader/CertificateQuizQuestionHeader'

const StyledContainer = styled(Container)`
    margin-top: ${spacing(5)};
`

export function CertificateQuizRoute() {
    const { _id } = useParams<{ _id: string }>()
    const navigate = useNavigate()

    const { data, loading, error, refetch } = useQuery<Pick<Query, 'publicCertificate'>, QueryPublicCertificateArgs>(
        PUBLIC_CERTIFICATE_DETAILS_QUERY,
        { variables: { _id: _id! } },
    )

    const [answerQuestion] = useMutation<Pick<Mutation, 'startPublicCertification'>, MutationAnswerQuestionArgs>(
        PUBLIC_CERTIFICATE_ANSWER_QUESTION_MUTATION,
    )

    useEffect(() => {
        switch (data?.publicCertificate?.state) {
            case CertificateState.Failed:
            case CertificateState.Passed: {
                navigate('../stats')
                break
            }
            case CertificateState.Pending: {
                navigate('..')
                break
            }
        }
    }, [data?.publicCertificate?.state, navigate])

    const certificate = data?.publicCertificate ?? null
    const question = certificate?.currentQuestion ?? null

    const onAnswer = async (answer: CertificateQuestionAnswerType) => {
        await answerQuestion({ variables: { certificateId: _id!, questionId: question._id, answer } })
    }

    const onCountdownEnd = () => refetch()

    return (
        <StyledContainer>
            {loading && <GenericLoading />}
            {error && <GenericError title="Da ist was schiefgelaufen" message={error.message} />}
            {!!question && !!certificate && (
                <>
                    <CertificateQuizQuestionHeader certificate={certificate} onCountdownEnd={onCountdownEnd} />
                    <CertificateQuizQuestion key={question._id} question={question} onAnswer={onAnswer} />
                </>
            )}
        </StyledContainer>
    )
}
